import "./styles.css";
import {
  ALLOWED_EXTENSIONS,
  MAX_FILES_PER_MESSAGE,
  MAX_FILE_BYTES,
  MAX_MESSAGE_FILES_BYTES,
  MAX_TEXT_CHARS,
  PROTOCOL_VERSION,
} from "../shared/constants";
import type {
  AgentInfo,
  ChatMessage,
  ClientFrame,
  FileMeta,
  MessageStatus,
  PermissionRequest,
  PermissionResponse,
  ServerFrame,
} from "../shared/protocol";
import {
  ApiError,
  deleteRemoteFile,
  login,
  logout,
  sessionStatus,
  uploadFile,
  type UploadHandle,
} from "./api";
import * as icons from "./icons";
import { enhanceMarkdown, renderMarkdown } from "./markdown";
import { toggleTheme, updateThemeButtons } from "./theme";

type ConnectionState = "connecting" | "open" | "closed";

interface UploadItem {
  key: string;
  name: string;
  size: number;
  file?: File;
  status: "uploading" | "ready" | "error";
  progress: number;
  remote?: FileMeta;
  error?: string;
  handle?: UploadHandle;
}

interface Elements {
  statusPill: HTMLElement;
  statusText: HTMLElement;
  composerStatus: HTMLElement;
  chatScroll: HTMLElement;
  chatColumn: HTMLElement;
  messageList: HTMLElement;
  permissionList: HTMLElement;
  emptyState: HTMLElement;
  jumpLatest: HTMLButtonElement;
  composerInput: HTMLTextAreaElement;
  sendBtn: HTMLButtonElement;
  attachBtn: HTMLButtonElement;
  fileInput: HTMLInputElement;
  uploadList: HTMLElement;
  composerMeta: HTMLElement;
  dragOverlay: HTMLElement;
  clearDialog: HTMLDialogElement;
  appRoot: HTMLElement;
}

interface AppState {
  authenticated: boolean;
  ws: WebSocket | null;
  wsState: ConnectionState;
  reconnectAttempt: number;
  reconnectTimer: number | null;
  pingTimer: number | null;
  intentionalClose: boolean;
  bridgeOnline: boolean;
  agent: AgentInfo | null;
  fallbackAvailable: boolean;
  conversationId: string;
  historyLoaded: boolean;
  messages: Map<string, ChatMessage>;
  messageNodes: Map<string, HTMLElement>;
  permissions: Map<string, PermissionRequest>;
  permissionNodes: Map<string, HTMLElement>;
  uploads: UploadItem[];
  renderTimers: Map<string, number>;
  loginBusy: boolean;
  passwordVisible: boolean;
}

const state: AppState = {
  authenticated: false,
  ws: null,
  wsState: "closed",
  reconnectAttempt: 0,
  reconnectTimer: null,
  pingTimer: null,
  intentionalClose: false,
  bridgeOnline: false,
  agent: null,
  fallbackAvailable: false,
  conversationId: "",
  historyLoaded: false,
  messages: new Map(),
  messageNodes: new Map(),
  permissions: new Map(),
  permissionNodes: new Map(),
  uploads: [],
  renderTimers: new Map(),
  loginBusy: false,
  passwordVisible: false,
};

let els: Elements | null = null;
const appElement = document.getElementById("app");
const toastElement = document.getElementById("toast-region");
const allowedExtensions = new Set<string>(ALLOWED_EXTENSIONS);
const acceptString = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",");

if (!appElement || !toastElement) throw new Error("Relay failed to start.");
const app: HTMLElement = appElement;
const toastRegion: HTMLElement = toastElement;

function element<T extends HTMLElement>(selector: string, root: ParentNode = document): T {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element: ${selector}`);
  return found;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toast(message: string, tone: "error" | "info" = "error"): void {
  const node = document.createElement("div");
  node.className = `toast toast--${tone}`;
  node.textContent = message;
  toastRegion.appendChild(node);
  window.setTimeout(() => {
    node.classList.add("toast--out");
    window.setTimeout(() => node.remove(), 220);
  }, 4200);
}

function fileNameExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

function fileIcon(): string {
  return icons.file(15);
}

function loginView(message = ""): void {
  state.authenticated = false;
  state.intentionalClose = true;
  state.passwordVisible = false;
  stopSocket();
  clearUploads(false);
  state.messages.clear();
  state.messageNodes.clear();
  state.permissions.clear();
  state.permissionNodes.clear();
  state.historyLoaded = false;
  els = null;

  app.innerHTML = `
    <div class="auth-shell">
      <header class="auth-header">
        <div class="brand brand--auth">
          <span class="brand-mark">${icons.mark(22)}</span>
          <span class="brand-name">Relay</span>
        </div>
        <button class="icon-button" type="button" data-action="theme"></button>
      </header>
      <main class="auth-main">
        <section class="auth-card" aria-labelledby="login-title">
          <div class="auth-mark">${icons.mark(34)}</div>
          <h1 id="login-title">Welcome back</h1>
          <p class="auth-subtitle">Enter your password to reach your agent.</p>
          <form id="login-form" novalidate>
            <label class="field-label" for="password">Password</label>
            <div class="password-field">
              <input id="password" name="password" type="password" autocomplete="current-password" spellcheck="false" required />
              <button class="password-toggle" type="button" data-action="reveal" aria-label="Show password"></button>
            </div>
            <p class="form-error" id="login-error" role="alert"></p>
            <button class="primary-button" id="login-submit" type="submit">Unlock</button>
          </form>
          <p class="auth-note">${icons.shield(15)} Password protected &middot; transport encrypted</p>
        </section>
      </main>
      <footer class="auth-footer">Private relay for your local agent</footer>
    </div>
  `;

  updateThemeButtons();
  const form = element<HTMLFormElement>("#login-form");
  const input = element<HTMLInputElement>("#password");
  const error = element<HTMLElement>("#login-error");
  const submit = element<HTMLButtonElement>("#login-submit");
  const reveal = element<HTMLButtonElement>('[data-action="reveal"]');

  if (message) error.textContent = message;
  reveal.innerHTML = state.passwordVisible ? icons.eyeOff() : icons.eye();
  reveal.addEventListener("click", () => {
    state.passwordVisible = !state.passwordVisible;
    input.type = state.passwordVisible ? "text" : "password";
    reveal.innerHTML = state.passwordVisible ? icons.eyeOff() : icons.eye();
    const label = state.passwordVisible ? "Hide password" : "Show password";
    reveal.setAttribute("aria-label", label);
    reveal.title = label;
    input.focus();
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (state.loginBusy) return;
    const password = input.value;
    if (!password) {
      error.textContent = "Enter your password.";
      input.focus();
      return;
    }

    state.loginBusy = true;
    submit.disabled = true;
    submit.textContent = "Checking…";
    error.textContent = "";

    try {
      await login(password);
      input.value = "";
      state.intentionalClose = false;
      renderApp();
      connectSocket();
    } catch (err) {
      const messageText =
        err instanceof ApiError ? err.message : "Could not sign in. Try again.";
      error.textContent = messageText;
      if (err instanceof ApiError && err.status === 429 && err.retryAfter) {
        let remaining = err.retryAfter;
        const original = messageText;
        const timer = window.setInterval(() => {
          remaining -= 1;
          if (remaining <= 0) {
            window.clearInterval(timer);
            error.textContent = "";
            submit.disabled = false;
            submit.textContent = "Unlock";
          } else {
            error.textContent = `${original} (${remaining}s)`;
          }
        }, 1000);
      } else {
        submit.disabled = false;
        submit.textContent = "Unlock";
      }
    } finally {
      state.loginBusy = false;
    }
  });

  window.setTimeout(() => input.focus(), 50);
}

function renderApp(): void {
  state.authenticated = true;
  state.intentionalClose = false;
  els = null;

  app.innerHTML = `
    <div class="app-shell">
      <header class="app-header">
        <div class="brand">
          <span class="brand-mark">${icons.mark(20)}</span>
          <span class="brand-name">Relay</span>
        </div>
        <div class="header-actions">
          <div class="status-pill" data-state="connecting" id="status-pill" title="Connecting to your relay">
            <span class="status-dot"></span>
            <span class="status-text" id="status-text">Connecting…</span>
          </div>
          <button class="icon-button" type="button" data-action="new-chat" title="New chat" aria-label="New chat">${icons.plus()}</button>
          <button class="icon-button" type="button" data-action="theme"></button>
          <button class="icon-button" type="button" data-action="logout" title="Sign out" aria-label="Sign out">${icons.logout()}</button>
        </div>
      </header>

      <main class="chat-scroll" id="chat-scroll">
        <div class="chat-column">
          <section class="empty-state" id="empty-state">
            <div class="empty-mark">${icons.mark(42)}</div>
            <h1>Your agent, within reach.</h1>
            <p>Send a message or attach a document to get started.</p>
            <div class="suggestions">
              <button class="suggestion" type="button" data-suggestion="Summarize a document I attach.">${icons.sparkle()} Summarize a document</button>
              <button class="suggestion" type="button" data-suggestion="Take a look at this code and suggest improvements.">${icons.sparkle()} Review some code</button>
              <button class="suggestion" type="button" data-suggestion="Help me plan my day.">${icons.sparkle()} Plan my day</button>
            </div>
          </section>
          <div class="message-list" id="message-list" role="log" aria-live="polite"></div>
          <div class="permission-list" id="permission-list"></div>
        </div>
        <button class="jump-latest" id="jump-latest" type="button" hidden>Jump to latest ${icons.arrowUp(14)}</button>
      </main>

      <footer class="composer-shell">
        <div class="composer-wrap">
          <div class="upload-list" id="upload-list"></div>
          <div class="composer" id="composer">
            <button class="icon-button attach-button" type="button" id="attach-button" title="Attach files" aria-label="Attach files">${icons.paperclip()}</button>
            <textarea id="composer-input" rows="1" placeholder="Message your agent…" aria-label="Message your agent" maxlength="${MAX_TEXT_CHARS}"></textarea>
            <button class="send-button" type="button" id="send-button" title="Send" aria-label="Send" disabled>${icons.arrowUp()}</button>
          </div>
          <div class="composer-meta" id="composer-meta">
            <span class="composer-hints">PDF, DOCX, XLSX, PPTX, TXT · up to 20 MB each</span>
            <span class="composer-hints composer-hints--keys">Enter to send · Shift+Enter for a new line</span>
            <span class="composer-status" id="composer-status"></span>
          </div>
        </div>
        <input type="file" id="file-input" multiple accept="${acceptString}" hidden />
        <div class="drag-overlay" id="drag-overlay" hidden>
          <div class="drag-card">${icons.paperclip(24)} Drop files to attach</div>
        </div>
      </footer>

      <dialog class="dialog" id="clear-dialog">
        <form method="dialog" class="dialog-card">
          <div class="dialog-icon">${icons.plus(24)}</div>
          <h2>Start a new chat?</h2>
          <p>This clears the conversation and uploaded files for every device.</p>
          <div class="dialog-actions">
            <button class="secondary-button" type="submit" value="cancel">Cancel</button>
            <button class="primary-button" type="button" id="confirm-clear">New chat</button>
          </div>
        </form>
      </dialog>
    </div>
  `;

  els = {
    statusPill: element<HTMLElement>("#status-pill"),
    statusText: element<HTMLElement>("#status-text"),
    composerStatus: element<HTMLElement>("#composer-status"),
    chatScroll: element<HTMLElement>("#chat-scroll"),
    chatColumn: element<HTMLElement>(".chat-column"),
    messageList: element<HTMLElement>("#message-list"),
    permissionList: element<HTMLElement>("#permission-list"),
    emptyState: element<HTMLElement>("#empty-state"),
    jumpLatest: element<HTMLButtonElement>("#jump-latest"),
    composerInput: element<HTMLTextAreaElement>("#composer-input"),
    sendBtn: element<HTMLButtonElement>("#send-button"),
    attachBtn: element<HTMLButtonElement>("#attach-button"),
    fileInput: element<HTMLInputElement>("#file-input"),
    uploadList: element<HTMLElement>("#upload-list"),
    composerMeta: element<HTMLElement>("#composer-meta"),
    dragOverlay: element<HTMLElement>("#drag-overlay"),
    clearDialog: element<HTMLDialogElement>("#clear-dialog"),
    appRoot: element<HTMLElement>(".app-shell"),
  };

  updateThemeButtons();
  updateConnectionUi();
  updateComposerState();
  updateEmptyState();
  bindAppEvents();
  window.setTimeout(() => els?.composerInput.focus(), 50);
}

function bindAppEvents(): void {
  if (!els) return;
  const {
    composerInput,
    sendBtn,
    attachBtn,
    fileInput,
    uploadList,
    clearDialog,
    chatScroll,
    jumpLatest,
    dragOverlay,
    permissionList,
    messageList,
  } = els;

  composerInput.addEventListener("input", () => {
    composerInput.style.height = "auto";
    composerInput.style.height = `${Math.min(composerInput.scrollHeight, 220)}px`;
    updateComposerState();
  });

  composerInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      void sendMessage();
    }
  });

  composerInput.addEventListener("paste", (event) => {
    const files = event.clipboardData?.files;
    if (files && files.length > 0) {
      event.preventDefault();
      addFiles(Array.from(files));
    }
  });

  sendBtn.addEventListener("click", () => void sendMessage());
  attachBtn.addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", () => {
    if (fileInput.files) addFiles(Array.from(fileInput.files));
    fileInput.value = "";
  });

  uploadList.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const remove = target.closest<HTMLButtonElement>("[data-remove-upload]");
    if (!remove) return;
    const key = remove.dataset.removeUpload;
    if (key) removeUpload(key);
  });

  chatScroll.addEventListener("scroll", () => {
    updateJumpButton();
  });

  jumpLatest.addEventListener("click", () => scrollToBottom(true));

  messageList.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const button = target.closest<HTMLButtonElement>(".code-copy");
    if (!button) return;
    const pre = button.closest("pre");
    const code = pre?.querySelector("code");
    if (!code) return;
    void navigator.clipboard
      .writeText(code.textContent ?? "")
      .then(() => {
        button.textContent = "Copied";
        window.setTimeout(() => {
          button.textContent = "Copy";
        }, 1400);
      })
      .catch(() => toast("Could not copy that code."));
  });

  permissionList.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const button = target.closest<HTMLButtonElement>("[data-permission-response]");
    if (!button) return;
    const id = button.dataset.permissionId;
    const response = button.dataset.permissionResponse as PermissionResponse | undefined;
    if (!id || !response) return;
    replyToPermission(id, response, button);
  });

  clearDialog.querySelector<HTMLButtonElement>("#confirm-clear")?.addEventListener("click", () => {
    clearDialog.close();
    clearConversation();
  });

  let dragDepth = 0;
  dragOverlay.addEventListener("dragenter", (event) => {
    event.preventDefault();
    dragDepth += 1;
    dragOverlay.hidden = false;
  });
  dragOverlay.addEventListener("dragover", (event) => event.preventDefault());
  dragOverlay.addEventListener("dragleave", (event) => {
    event.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) dragOverlay.hidden = true;
  });
  dragOverlay.addEventListener("drop", (event) => {
    event.preventDefault();
    dragDepth = 0;
    dragOverlay.hidden = true;
    if (event.dataTransfer?.files.length) addFiles(Array.from(event.dataTransfer.files));
  });

}

function bindWindowDrag(): void {
  let depth = 0;
  window.addEventListener("dragenter", (event) => {
    if (!state.authenticated || !event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    depth += 1;
    if (els) els.dragOverlay.hidden = false;
  });
  window.addEventListener("dragover", (event) => {
    if (!state.authenticated || !event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
  });
  window.addEventListener("dragleave", (event) => {
    if (!state.authenticated) return;
    depth = Math.max(0, depth - 1);
    if (depth === 0 && els) els.dragOverlay.hidden = true;
  });
  window.addEventListener("drop", (event) => {
    if (!state.authenticated || !event.dataTransfer?.files.length) return;
    event.preventDefault();
    depth = 0;
    if (els) els.dragOverlay.hidden = true;
    addFiles(Array.from(event.dataTransfer.files));
  });
}

function updateConnectionUi(): void {
  if (!els) return;
  const { statusPill, statusText } = els;
  let label = "Reconnecting…";
  let pillState = "offline";

  if (state.wsState === "open") {
    if (state.bridgeOnline) {
      if (state.agent?.fallback) {
        label = "Fallback agent";
        pillState = "fallback";
      } else {
        label = "Agent online";
        pillState = "online";
      }
    } else if (state.fallbackAvailable) {
      label = "Fallback agent";
      pillState = "fallback";
    } else {
      label = "Agent offline";
      pillState = "offline";
    }
  } else if (state.wsState === "connecting") {
    label = "Connecting…";
    pillState = "connecting";
  }

  statusText.textContent = label;
  statusPill.dataset.state = pillState;

  const agentTitle = state.agent
    ? [
        state.agent.fallback ? "Fallback" : null,
        state.agent.mode,
        state.agent.model,
      ]
        .filter(Boolean)
        .join(" · ")
    : state.bridgeOnline
      ? "Agent connected"
      : state.fallbackAvailable
        ? "Local agent offline · fallback ready"
        : "Start the local bridge to connect your agent";
  statusPill.title = agentTitle;
  updateComposerState();
}

function setHistoryLoading(): void {
  if (!els) return;
  els.emptyState.classList.toggle("empty-state--loading", !state.historyLoaded);
}

function updateEmptyState(): void {
  if (!els) return;
  const empty = state.messages.size === 0 && state.historyLoaded;
  els.emptyState.hidden = !empty;
  els.emptyState.classList.remove("empty-state--loading");
}

function updateJumpButton(): void {
  if (!els) return;
  const show = !isNearBottom() && state.messages.size > 0;
  els.jumpLatest.hidden = !show;
}

function isNearBottom(): boolean {
  if (!els) return true;
  return (
    els.chatScroll.scrollHeight -
      els.chatScroll.scrollTop -
      els.chatScroll.clientHeight <
    120
  );
}

function scrollToBottom(smooth = false): void {
  if (!els) return;
  els.chatScroll.scrollTo({
    top: els.chatScroll.scrollHeight,
    behavior: smooth ? "smooth" : "auto",
  });
  updateJumpButton();
}

function updateComposerState(): void {
  if (!els) return;
  const { composerInput, sendBtn, attachBtn, composerStatus } = els;
  const text = composerInput.value.trim();
  const uploading = state.uploads.some((item) => item.status === "uploading");
  const ready = state.uploads.filter((item) => item.status === "ready").length;
  const canSend =
    state.wsState === "open" &&
    !uploading &&
    (text.length > 0 || ready > 0) &&
    composerInput.value.length <= MAX_TEXT_CHARS;

  sendBtn.disabled = !canSend;
  attachBtn.disabled = state.uploads.length >= MAX_FILES_PER_MESSAGE;
  composerStatus.textContent = uploading
    ? "Uploading attachments…"
    : state.wsState !== "open"
      ? "Reconnecting to the relay…"
      : "";
}

function connectSocket(): void {
  if (state.ws) return;
  state.wsState = "connecting";
  state.historyLoaded = false;
  setHistoryLoading();
  updateConnectionUi();

  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(`${protocol}//${location.host}/api/ws`);
  state.ws = socket;

  socket.addEventListener("open", () => {
    state.wsState = "open";
    state.reconnectAttempt = 0;
    state.intentionalClose = false;
    updateConnectionUi();
    startPing();
  });

  socket.addEventListener("message", (event) => {
    if (typeof event.data !== "string") return;
    handleServerFrame(event.data);
  });

  socket.addEventListener("close", (event) => {
    stopPing();
    state.ws = null;
    state.wsState = "closed";
    state.bridgeOnline = false;
    state.agent = null;
    state.historyLoaded = false;
    updateConnectionUi();
    if (event.code === 4001) {
      showExpiredSession();
      return;
    }
    if (!state.intentionalClose && state.authenticated) scheduleReconnect();
  });

  socket.addEventListener("error", () => {
    // The close event drives reconnection.
  });
}

function stopSocket(): void {
  stopPing();
  if (state.reconnectTimer) {
    window.clearTimeout(state.reconnectTimer);
    state.reconnectTimer = null;
  }
  const socket = state.ws;
  state.ws = null;
  state.wsState = "closed";
  if (socket) {
    try {
      socket.close(1000, "Client closing");
    } catch {
      // Ignore.
    }
  }
}

function scheduleReconnect(): void {
  if (state.reconnectTimer || !state.authenticated) return;
  const base = Math.min(15000, 600 * 2 ** Math.min(state.reconnectAttempt, 5));
  const delay = base + Math.floor(Math.random() * 400);
  state.reconnectAttempt += 1;
  state.reconnectTimer = window.setTimeout(() => {
    state.reconnectTimer = null;
    if (state.authenticated) connectSocket();
  }, delay);
}

function startPing(): void {
  stopPing();
  state.pingTimer = window.setInterval(() => {
    if (state.ws?.readyState === WebSocket.OPEN) state.ws.send("ping");
  }, 25000);
}

function stopPing(): void {
  if (state.pingTimer) {
    window.clearInterval(state.pingTimer);
    state.pingTimer = null;
  }
}

function sendFrame(frame: ClientFrame, reportError = true): boolean {
  if (state.ws?.readyState !== WebSocket.OPEN) {
    if (reportError) toast("Reconnecting to the relay. Try again in a moment.");
    return false;
  }
  try {
    state.ws.send(JSON.stringify(frame));
    return true;
  } catch {
    if (reportError) toast("Could not reach the relay.");
    return false;
  }
}

function showExpiredSession(): void {
  state.authenticated = false;
  state.intentionalClose = true;
  stopSocket();
  loginView("Your session expired. Please sign in again.");
}

async function handleLogout(): Promise<void> {
  if (!state.authenticated) return;
  state.intentionalClose = true;
  stopSocket();
  await logout();
  loginView();
}

function handleServerFrame(raw: string): void {
  let frame: ServerFrame;
  try {
    frame = JSON.parse(raw) as ServerFrame;
  } catch {
    return;
  }
  if (!frame || frame.v !== PROTOCOL_VERSION) return;

  switch (frame.type) {
    case "init": {
      state.bridgeOnline = frame.bridgeOnline;
      state.agent = frame.agent;
      state.fallbackAvailable = frame.fallbackAvailable === true;
      if (state.conversationId && state.conversationId !== frame.conversationId) {
        clearMessageState();
      }
      state.conversationId = frame.conversationId;
      state.historyLoaded = false;
      for (const timer of state.renderTimers.values()) window.clearTimeout(timer);
      state.renderTimers.clear();
      clearPermissionState();
      updateConnectionUi();
      setHistoryLoading();
      break;
    }
    case "history": {
      upsertMessage(frame.message, false);
      break;
    }
    case "ready": {
      state.historyLoaded = true;
      updateEmptyState();
      updateJumpButton();
      if (isNearBottom()) scrollToBottom();
      break;
    }
    case "user": {
      upsertMessage(frame.message, true);
      break;
    }
    case "assistant": {
      upsertMessage(frame.message, true);
      break;
    }
    case "delta": {
      handleDelta(frame.id, frame.text);
      break;
    }
    case "done": {
      cancelRender(frame.message.id);
      upsertMessage(frame.message, true, true);
      break;
    }
    case "error": {
      cancelRender(frame.id);
      handleAssistantError(frame.id, frame.message);
      break;
    }
    case "status": {
      state.bridgeOnline = frame.bridgeOnline;
      state.agent = frame.agent;
      state.fallbackAvailable = frame.fallbackAvailable === true;
      updateConnectionUi();
      break;
    }
    case "permission": {
      addPermission(frame.permission);
      break;
    }
    case "permission_resolved": {
      removePermission(frame.permissionId);
      break;
    }
    case "cleared": {
      clearMessageState();
      clearPermissionState();
      clearUploads(true);
      state.historyLoaded = true;
      updateEmptyState();
      updateJumpButton();
      break;
    }
    case "fatal": {
      toast(frame.message);
      break;
    }
    default:
      break;
  }
}

function clearMessageState(): void {
  state.messages.clear();
  state.messageNodes.clear();
  for (const timer of state.renderTimers.values()) window.clearTimeout(timer);
  state.renderTimers.clear();
  if (els) {
    els.messageList.replaceChildren();
    els.permissionList.replaceChildren();
    updateEmptyState();
    updateJumpButton();
  }
}

function createMessageNode(message: ChatMessage): HTMLElement {
  const article = document.createElement("article");
  article.className = `message message--${message.role}`;
  article.dataset.id = message.id;

  if (message.role === "user") {
    const bubble = document.createElement("div");
    bubble.className = "message-bubble";
    const files = document.createElement("div");
    files.className = "message-files";
    const text = document.createElement("div");
    text.className = "message-text";
    bubble.append(files, text);
    article.append(bubble);
  } else {
    const avatar = document.createElement("div");
    avatar.className = "message-avatar";
    avatar.innerHTML = icons.mark(17);
    const body = document.createElement("div");
    body.className = "message-body";
    const content = document.createElement("div");
    content.className = "message-content";
    const cursor = document.createElement("span");
    cursor.className = "stream-cursor";
    cursor.setAttribute("aria-hidden", "true");
    const status = document.createElement("div");
    status.className = "message-status";
    body.append(content, cursor, status);
    article.append(avatar, body);
  }

  return article;
}

function upsertMessage(
  message: ChatMessage,
  render: boolean,
  forceRender = false,
): void {
  if (!els) return;
  const isNew = !state.messages.has(message.id);
  state.messages.set(message.id, message);

  let node = state.messageNodes.get(message.id);
  if (!node) {
    node = createMessageNode(message);
    state.messageNodes.set(message.id, node);
    els.messageList.appendChild(node);
  }

  if (isNew) {
    updateEmptyState();
    if (message.role === "user" || isNearBottom()) scrollToBottom();
  }

  if (render || isNew || forceRender) {
    updateMessageNode(node, message);
    updateJumpButton();
  }
}

function updateMessageNode(node: HTMLElement, message: ChatMessage): void {
  node.className = `message message--${message.role} message--${message.status}`;
  node.dataset.status = message.status;

  if (message.role === "user") {
    const files = node.querySelector<HTMLElement>(".message-files");
    const text = node.querySelector<HTMLElement>(".message-text");
    if (files) renderMessageFiles(files, message.files);
    if (text) {
      text.textContent = message.text;
      text.hidden = message.text.length === 0;
    }
    return;
  }

  const content = node.querySelector<HTMLElement>(".message-content");
  const status = node.querySelector<HTMLElement>(".message-status");
  const cursor = node.querySelector<HTMLElement>(".stream-cursor");
  if (!content || !status || !cursor) return;

  const hasText = message.text.trim().length > 0;
  content.hidden = !hasText;
  if (hasText) {
    if (message.status === "error") {
      content.textContent = message.text;
      content.classList.add("message-content--error");
    } else {
      try {
        content.innerHTML = renderMarkdown(message.text);
        content.classList.remove("message-content--error");
        enhanceMarkdown(content);
      } catch {
        content.textContent = message.text;
        content.classList.remove("message-content--error");
      }
    }
  } else {
    content.textContent = "";
    content.classList.remove("message-content--error");
  }

  cursor.hidden = !(message.status === "streaming" && hasText);
  node.classList.toggle("message--thinking", !hasText && (message.status === "queued" || message.status === "pending"));

  if (message.status === "queued") {
    status.textContent = "Queued — waiting for the agent.";
    status.hidden = false;
  } else if (message.status === "pending") {
    status.textContent = "Agent is thinking…";
    status.hidden = false;
  } else if (message.status === "streaming") {
    status.textContent = hasText ? "" : "Agent is thinking…";
    status.hidden = hasText;
  } else if (message.status === "error") {
    status.textContent = "The agent could not complete this request.";
    status.hidden = !hasText;
  } else {
    status.textContent = "";
    status.hidden = true;
  }
}

function handleDelta(id: string, text: string): void {
  const existing = state.messages.get(id);
  if (!existing) {
    const message: ChatMessage = {
      id,
      role: "assistant",
      text,
      files: [],
      status: "streaming",
      createdAt: Date.now(),
    };
    state.messages.set(id, message);
    const node = createMessageNode(message);
    state.messageNodes.set(id, node);
    els?.messageList.appendChild(node);
    updateMessageNode(node, message);
    updateEmptyState();
    return;
  }

  existing.text += text;
  existing.status = "streaming";
  scheduleRender(id);
  if (isNearBottom()) scrollToBottom();
}

function handleAssistantError(id: string, errorText: string): void {
  const existing = state.messages.get(id);
  if (existing) {
    existing.text = errorText;
    existing.status = "error";
    const node = state.messageNodes.get(id);
    if (node) updateMessageNode(node, existing);
  } else {
    const message: ChatMessage = {
      id,
      role: "assistant",
      text: errorText,
      files: [],
      status: "error",
      createdAt: Date.now(),
    };
    state.messages.set(id, message);
    const node = createMessageNode(message);
    state.messageNodes.set(id, node);
    els?.messageList.appendChild(node);
    updateMessageNode(node, message);
  }
  updateEmptyState();
  if (isNearBottom()) scrollToBottom();
}

function scheduleRender(id: string): void {
  if (state.renderTimers.has(id)) return;
  const timer = window.setTimeout(() => {
    state.renderTimers.delete(id);
    const message = state.messages.get(id);
    const node = state.messageNodes.get(id);
    if (message && node) {
      updateMessageNode(node, message);
      updateJumpButton();
    }
  }, 60);
  state.renderTimers.set(id, timer);
}

function cancelRender(id: string): void {
  const timer = state.renderTimers.get(id);
  if (timer) {
    window.clearTimeout(timer);
    state.renderTimers.delete(id);
  }
}

function renderMessageFiles(container: HTMLElement, files: FileMeta[]): void {
  container.replaceChildren();
  if (files.length === 0) {
    container.hidden = true;
    return;
  }
  container.hidden = false;
  for (const file of files) {
    const link = document.createElement("a");
    link.className = "message-file";
    link.href = `/api/files/${encodeURIComponent(file.id)}`;
    link.title = `Download ${file.name}`;
    const icon = document.createElement("span");
    icon.className = "message-file-icon";
    icon.innerHTML = fileIcon();
    const name = document.createElement("span");
    name.className = "message-file-name";
    name.textContent = file.name;
    const size = document.createElement("span");
    size.className = "message-file-size";
    size.textContent = formatBytes(file.size);
    link.append(icon, name, size);
    container.appendChild(link);
  }
}

function addPermission(permission: PermissionRequest): void {
  if (!els) return;
  if (state.permissions.has(permission.id)) {
    removePermission(permission.id);
  }
  state.permissions.set(permission.id, permission);

  const card = document.createElement("section");
  card.className = "permission-card";
  card.dataset.permissionId = permission.id;

  const header = document.createElement("div");
  header.className = "permission-header";
  const icon = document.createElement("span");
  icon.className = "permission-icon";
  icon.innerHTML = icons.shield(18);
  const titleWrap = document.createElement("div");
  const eyebrow = document.createElement("p");
  eyebrow.className = "permission-eyebrow";
  eyebrow.textContent = "Agent permission";
  const title = document.createElement("h2");
  title.textContent = permission.title;
  titleWrap.append(eyebrow, title);
  header.append(icon, titleWrap);

  const meta = document.createElement("p");
  meta.className = "permission-meta";
  const pattern = Array.isArray(permission.pattern)
    ? permission.pattern.join(", ")
    : permission.pattern;
  meta.textContent = pattern ? `${permission.type} · ${pattern}` : permission.type;

  card.append(header, meta);

  if (permission.metadata && Object.keys(permission.metadata).length > 0) {
    const details = document.createElement("details");
    details.className = "permission-details";
    const summary = document.createElement("summary");
    summary.textContent = "Details";
    const pre = document.createElement("pre");
    try {
      pre.textContent = JSON.stringify(permission.metadata, null, 2).slice(0, 4000);
    } catch {
      pre.textContent = "Could not display details.";
    }
    details.append(summary, pre);
    card.appendChild(details);
  }

  const actions = document.createElement("div");
  actions.className = "permission-actions";
  const allow = document.createElement("button");
  allow.type = "button";
  allow.className = "primary-button primary-button--small";
  allow.textContent = "Allow once";
  allow.dataset.permissionId = permission.id;
  allow.dataset.permissionResponse = "once";
  const always = document.createElement("button");
  always.type = "button";
  always.className = "secondary-button secondary-button--small";
  always.textContent = "Always allow";
  always.dataset.permissionId = permission.id;
  always.dataset.permissionResponse = "always";
  const deny = document.createElement("button");
  deny.type = "button";
  deny.className = "danger-button";
  deny.textContent = "Deny";
  deny.dataset.permissionId = permission.id;
  deny.dataset.permissionResponse = "reject";
  actions.append(allow, always, deny);
  card.appendChild(actions);

  state.permissionNodes.set(permission.id, card);
  els.permissionList.appendChild(card);
  if (isNearBottom()) scrollToBottom();
}

function removePermission(id: string): void {
  const card = state.permissionNodes.get(id);
  if (card) card.remove();
  state.permissionNodes.delete(id);
  state.permissions.delete(id);
}

function clearPermissionState(): void {
  state.permissions.clear();
  state.permissionNodes.clear();
  if (els) els.permissionList.replaceChildren();
}

function replyToPermission(
  permissionId: string,
  response: PermissionResponse,
  button: HTMLButtonElement,
): void {
  if (!state.permissions.has(permissionId)) return;
  const card = state.permissionNodes.get(permissionId);
  card?.querySelectorAll<HTMLButtonElement>("button").forEach((item) => {
    item.disabled = true;
  });
  button.textContent = "Responding…";

  const sent = sendFrame({
    v: PROTOCOL_VERSION,
    type: "permission_reply",
    permissionId,
    response,
  });
  if (!sent) {
    card?.querySelectorAll<HTMLButtonElement>("button").forEach((item) => {
      item.disabled = false;
    });
    const labels: Record<PermissionResponse, string> = {
      once: "Allow once",
      always: "Always allow",
      reject: "Deny",
    };
    button.textContent = labels[response];
  }
}

function addFiles(files: File[]): void {
  if (!state.authenticated || files.length === 0) return;
  for (const file of files) {
    if (state.uploads.length >= MAX_FILES_PER_MESSAGE) {
      toast(`Attach at most ${MAX_FILES_PER_MESSAGE} files per message.`);
      break;
    }
    const extension = fileNameExtension(file.name);
    if (!allowedExtensions.has(extension)) {
      toast(`${file.name}: unsupported file type.`);
      continue;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast(`${file.name} is larger than 20 MB.`);
      continue;
    }
    if (file.size === 0) {
      toast(`${file.name} is empty.`);
      continue;
    }
    const currentTotal = state.uploads.reduce((total, item) => total + item.size, 0);
    if (currentTotal + file.size > MAX_MESSAGE_FILES_BYTES) {
      toast("Attachments total more than 40 MB.");
      continue;
    }

    const item: UploadItem = {
      key: crypto.randomUUID(),
      name: file.name,
      size: file.size,
      file,
      status: "uploading",
      progress: 0,
    };
    state.uploads.push(item);
    startUpload(item);
  }
  renderUploads();
  updateComposerState();
}

function startUpload(item: UploadItem): void {
  if (!item.file) return;
  item.handle = uploadFile(
    item.file,
    (percent) => {
      item.progress = percent;
      renderUploads();
    },
    (remote) => {
      item.status = "ready";
      item.remote = remote;
      item.progress = 100;
      renderUploads();
      updateComposerState();
    },
    (error) => {
      if (error.code === "cancelled") return;
      item.status = "error";
      item.error = error.message;
      renderUploads();
      updateComposerState();
      if (error.status === 401) {
        showExpiredSession();
      } else {
        toast(`${item.name}: ${error.message}`);
      }
    },
  );
}

function removeUpload(key: string): void {
  const index = state.uploads.findIndex((item) => item.key === key);
  if (index < 0) return;
  const item = state.uploads[index];
  if (!item) return;
  item.handle?.abort();
  if (item.remote) void deleteRemoteFile(item.remote.id);
  state.uploads.splice(index, 1);
  renderUploads();
  updateComposerState();
}

function clearUploads(deleteRemote: boolean): void {
  for (const item of state.uploads) {
    item.handle?.abort();
    if (deleteRemote && item.remote) void deleteRemoteFile(item.remote.id);
  }
  state.uploads = [];
  renderUploads();
  updateComposerState();
}

function renderUploads(): void {
  if (!els) return;
  const container = els.uploadList;
  container.replaceChildren();
  if (state.uploads.length === 0) {
    container.hidden = true;
    return;
  }
  container.hidden = false;

  for (const item of state.uploads) {
    const chip = document.createElement("div");
    chip.className = `upload-chip upload-chip--${item.status}`;
    const icon = document.createElement("span");
    icon.className = "upload-icon";
    icon.innerHTML = fileIcon();
    const body = document.createElement("div");
    body.className = "upload-body";
    const name = document.createElement("span");
    name.className = "upload-name";
    name.textContent = item.name;
    const meta = document.createElement("span");
    meta.className = "upload-meta";
    meta.textContent =
      item.status === "uploading"
        ? `Uploading ${item.progress}%`
        : item.status === "error"
          ? item.error ?? "Upload failed"
          : formatBytes(item.size);

    body.append(name, meta);

    if (item.status === "uploading") {
      const progress = document.createElement("span");
      progress.className = "upload-progress";
      const fill = document.createElement("span");
      fill.className = "upload-progress-fill";
      fill.style.width = `${item.progress}%`;
      progress.appendChild(fill);
      body.appendChild(progress);
    }

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "upload-remove";
    remove.dataset.removeUpload = item.key;
    remove.setAttribute("aria-label", `Remove ${item.name}`);
    remove.innerHTML = icons.x(14);

    chip.append(icon, body, remove);
    container.appendChild(chip);
  }
}

async function sendMessage(): Promise<void> {
  if (!els || state.wsState !== "open") return;
  const text = els.composerInput.value;
  if (text.length > MAX_TEXT_CHARS) {
    toast("That message is too long.");
    return;
  }
  const ready = state.uploads.filter(
    (item): item is UploadItem & { remote: FileMeta } => item.status === "ready" && Boolean(item.remote),
  );
  if (state.uploads.some((item) => item.status === "uploading")) {
    toast("Wait for the uploads to finish.");
    return;
  }
  if (!text.trim() && ready.length === 0) return;

  const id = crypto.randomUUID();
  const files = ready.map((item) => item.remote);
  const message: ChatMessage = {
    id,
    role: "user",
    text,
    files,
    status: "done",
    createdAt: Date.now(),
  };

  upsertMessage(message, true, true);
  const sent = sendFrame({
    v: PROTOCOL_VERSION,
    type: "chat",
    id,
    text,
    files,
    ts: Date.now(),
  });
  if (!sent) {
    message.status = "error";
    const node = state.messageNodes.get(id);
    if (node) updateMessageNode(node, message);
    return;
  }

  els.composerInput.value = "";
  els.composerInput.style.height = "auto";
  state.uploads = [];
  renderUploads();
  updateComposerState();
  scrollToBottom();
}

function clearConversation(): void {
  if (!state.authenticated) return;
  if (sendFrame({ v: PROTOCOL_VERSION, type: "clear" })) {
    // The relay confirms with a cleared frame.
  }
}

function openClearDialog(): void {
  if (!els) return;
  if (state.messages.size === 0) {
    clearConversation();
    return;
  }
  if (typeof els.clearDialog.showModal === "function") {
    els.clearDialog.showModal();
  } else {
    clearConversation();
  }
}

async function boot(): Promise<void> {
  updateThemeButtons();
  bindWindowDrag();
  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-action="theme"]')) {
      toggleTheme();
      return;
    }
    if (target.closest('[data-action="logout"]')) {
      void handleLogout();
      return;
    }
    if (target.closest('[data-action="new-chat"]')) {
      openClearDialog();
      return;
    }
    const suggestion = target.closest<HTMLElement>("[data-suggestion]");
    if (suggestion && els) {
      els.composerInput.value = suggestion.dataset.suggestion ?? "";
      els.composerInput.dispatchEvent(new Event("input"));
      els.composerInput.focus();
    }
  });

  try {
    const status = await sessionStatus();
    if (status.authenticated) {
      renderApp();
      connectSocket();
    } else {
      loginView();
    }
  } catch (error) {
    const message =
      error instanceof ApiError && error.status !== 401
        ? error.message
        : "";
    loginView(message);
  }
}

window.addEventListener("beforeunload", () => {
  state.intentionalClose = true;
  stopSocket();
});

void boot();
